import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

// Scan results for one organization, for both sources.
//
// Two things here are easy to get wrong and are deliberate:
//  - both list endpoints are POST, not GET, even though they only read
//  - the internal and external stores are different tables with different columns and
//    different delete routes, which is why this hook takes a source rather than
//    pretending they are one list
export function useScanResults(organizationId, source /* 'internal' | 'external' */) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const data = source === "internal"
        ? await api.post("s2/api/scan_results", { organizationid: organizationId })
        : await api.post("/s2/api/external_risk", { organizationid: Number(organizationId) });
      setResults(data?.result || []);
      setError("");
    } catch (e) {
      setError(e.message || "Could not load scan results.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, source]);

  useEffect(() => { load(); }, [load]);

  const remove = useCallback(async (id) => {
    setBusyId(id);
    try {
      if (source === "internal") await api.del(`s2/api/scan_results/${id}`);
      else await api.del(`/s2/api/external_risk/${id}`);
      const key = source === "internal" ? "scan_id" : "id";
      setResults((prev) => prev.filter((r) => r[key] !== id));
    } finally {
      setBusyId(null);
    }
  }, [source]);

  // Scores the file server-side, then refetches so the row shows the new score.
  const analyze = useCallback(async (filePath, rowKey) => {
    setBusyId(rowKey);
    try {
      await api.post("s3/api/score/", { fileUrl: filePath });
      await load();
    } finally {
      setBusyId(null);
    }
  }, [load]);

  // Opens the raw scan JSON in a new tab. The blob URL is revoked on load rather than
  // immediately, or the tab races the revoke and opens blank.
  const view = useCallback(async (filePath, rowKey) => {
    setBusyId(rowKey);
    try {
      const data = await api.post("s3/api/read", { fileUrl: filePath });
      const blob = new Blob([JSON.stringify(data?.view, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const tab = window.open(url, "_blank");
      if (tab) tab.onload = () => URL.revokeObjectURL(url);
    } finally {
      setBusyId(null);
    }
  }, []);

  return { results, loading, error, busyId, reload: load, remove, analyze, view };
}
